import React, { useState, useEffect } from 'react';
import { 
  Container,
  Box,
  Typography,
  Avatar,
  Button,
  Divider,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  CircularProgress,
  useMediaQuery
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import Layout from '../components/Layout';
import { useNavigate } from 'react-router-dom';
import LogoutIcon from '@mui/icons-material/Logout';

const Settings = () => {  
  const [user, setUser] = useState({
    displayName: '',
    email: '',
    avatarUrl: '',
    userId: '',
    userEmail: ''
  });
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [tempName, setTempName] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    fetchUserData();
  }, []);

  const navigate = useNavigate();

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://kast-server-713b1c89d478.herokuapp.com/api/auth/currentUser', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setUser(data);
      setPreviewImage(data.profilePicture || '');
      setTempName(data.displayName);
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.match('image.*')) {
        setError('Please select an image file (JPEG, PNG)');
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        setError('Image size should be less than 2MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        setPreviewImage(event.target.result);
      };
      reader.readAsDataURL(file);
      setError('');
    }
  };

  const handleSaveClick = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('token');
      const userId = user.userId;
      
      const updateData = {
        displayName: tempName,
        ...(previewImage && { profilePicture: previewImage })
      };

      const response = await fetch('https://kast-server-713b1c89d478.herokuapp.com/api/auth/update-profile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          userId,
          ...updateData
        })
      });      
      
      if (response.ok) {
        setUser({ 
          ...user, 
          displayName: tempName,
          ...(previewImage && { profilePicture: previewImage })
        });
        setIsEditing(false);
        setLoading(true)
        setMsg("Profile updated");
        setError("");
      } else {
        throw new Error('Failed to update profile');
        setLoading(true)
      }
    } catch (error) {
      setLoading(true)
      console.error('Error updating profile:', error);
      setError("Failed to update profile");
      setMsg("");
    }
  };

  const handleDeleteAccount = async () => {
    try {
      const token = localStorage.getItem('token');
      const userId = user.userId;
      
      await fetch('https://kast-server-713b1c89d478.herokuapp.com/api/auth/delete-user', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userId })
      });

      await fetch('https://kast-server-713b1c89d478.herokuapp.com/api/auth/delete-user-data', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userId })
      });

      localStorage.removeItem('token');
      navigate('/');
    } catch (error) {
      console.error('Error deleting account:', error);
      setError("Failed to delete account");
    }
  };

  return (
    <Layout>
      <Container maxWidth="md" sx={{ py: 3 }}>        
        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
        {msg && <Alert severity="success" sx={{ mb: 3 }}>{msg}</Alert>}

        {/* Profile Section */}
        <Box sx={{ 
          bgcolor: '#fefaf5', 
          border: '1px solid black', 
          borderRadius: '12px',
          p: 3,
          mb: 3
        }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
          Your Profile
        </Typography>
         <Box 
            sx={{ 
              mb: 3, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between' 
            }}
          >
            {/* Label on the left */}
            <Typography variant="subtitle2" sx={{ fontWeight: 500, fontSize: 18 }}>
              Name
            </Typography>

            {/* Right-side content (TextField or Box) */}
            {isEditing ? (
              <TextField
                value={tempName}
                onChange={(e) => setTempName(e.target.value)}
                sx={{
                  width: '120px', // smaller width
                  backgroundColor: '#fefaf5',
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                  }
                }}
              />
            ) : (
              <Box
                sx={{                  
                  border: '1px solid black',
                  borderRadius: '12px',
                  padding: '10px 16px',
                  backgroundColor:'#fefaf5',
                  textAlign: 'left'
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {user.displayName}
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ 
              mb: 4, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between' 
            }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1, fontSize: 18 }}>
              Email
            </Typography>
               <Box
                sx={{                 
                  border: '1px solid black',
                  borderRadius: '12px',
                  padding: '10px 16px',
                  backgroundColor:'#fefaf5',
                  textAlign: 'left'
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {user.userEmail}
                </Typography>
              </Box>
          </Box>

          <Box  sx={{ 
              mb: 3, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between' 
            }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1, fontSize: 18 }}>
              Profile Picture
            </Typography>
            <Box sx={{ alignItems: 'center', gap: 2 }}>
              <Avatar 
                src={previewImage || user.profilePicture || `https://ui-avatars.com/api/?name=${user.displayName}&background=FF890A&color=fff`}
                sx={{ width: 64, height: 64, mb: 2 }}
              >
                {!previewImage && !user.profilePicture && user.displayName?.charAt(0)}
              </Avatar>
              {isEditing && (
                <Button 
                  variant="outlined"
                  component="label"
                  sx={{ textTransform: 'none', fontSize:'16px' }}
                >
                  Change
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                </Button>
              )}
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {isEditing ? (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button 
                variant="outlined" 
                sx={{ textTransform: 'none', fontSize:'16px' }}
                onClick={() => {
                  setIsEditing(false);
                  setTempName(user.displayName);
                  setPreviewImage(user.profilePicture || '');
                }}
              >
                Cancel
              </Button>
              <Button               
                variant="contained" 
                onClick={handleSaveClick}
                sx={{
                  backgroundColor: '#FF890A',
                  '&:hover': { backgroundColor: '#e67a09' },                  
                  textTransform: 'none'
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Save'}
              </Button>
            </Box>
          ) : (
            <Button 
              variant="outlined" 
              startIcon={<Edit />}
              onClick={handleEditClick}
              sx={{ 
                mt: 2,
                fontSize:'16px',
                textTransform: 'none' // This disables the uppercase transformation
              }}
            >
              Edit Profile
            </Button>
          )}
        </Box>

        {/* Manage Account Section */}
        <Box sx={{ 
          bgcolor: '#fefaf5', 
          border: '1px solid black', 
          borderRadius: '12px',
          p: 3,
          mb: 3
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Manage Account
          </Typography>
         
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 500, fontSize: 18}}>Log out</Typography>
            <IconButton color="primary" onClick={() => {
              localStorage.removeItem('token');
              navigate('/');
            }}>
              <LogoutIcon sx={{ fontSize: isMobile ? '25px' : '30px' }}/>
            </IconButton>
          </Box>        
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 500, fontSize: 18}} >Delete Account</Typography>
            <IconButton color="error"  onClick={() => setDeleteConfirmOpen(true)}>
              <Delete sx={{ fontSize: isMobile ? '25px' : '30px' }}/>
            </IconButton>
          </Box>        
        </Box>          
      </Container>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirm Account Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete your account? This action cannot be undone.
            All your data including links, likes, and saves will be permanently removed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button sx={{ textTransform: 'none', fontSize:'16px' }} onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button   
            sx={{ textTransform: 'none', fontSize:'16px' }}        
            onClick={() => {
              setDeleteConfirmOpen(false);
              handleDeleteAccount();
            }}
            color="error"
          >
            Delete Account
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default Settings;